import { useEffect, useState } from "react";
import { X, Printer, FileText, Bluetooth, Usb, Share2 } from "lucide-react";
import { api, downloadAuthed, API, getAuthToken } from "@/lib/api";

/**
 * Receipt printer modal for 80mm thermal printers (OCOM OCPP-80T).
 * - Renders an on-screen 80mm preview by fetching the PDF and embedding it
 * - Supports Web Bluetooth (BLE) and WebUSB raw ESC/POS transmission
 * - Falls back to "Print PDF" for any non-thermal printer (e.g. Xerox A4)
 * - Optional auto paper cut + cash drawer kick
 */
export default function ReceiptPrinter({ open, sale, onClose, docType = "preventiv", currency = null, onPrinted }) {
    const docOpts = { doc_type: docType, ...(currency ? { currency } : {}) };
    const [vat, setVat] = useState(0);
    const [copies, setCopies] = useState(1);
    const [cutPaper, setCutPaper] = useState(true);
    const [openDrawer, setOpenDrawer] = useState(false);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [printing, setPrinting] = useState(false);
    const [btDevice, setBtDevice] = useState(null);

    // Build preview PDF whenever options change
    useEffect(() => {
        if (!open || !sale) return;
        let revoked = false;
        let oldUrl = null;
        (async () => {
            try {
                const token = getAuthToken();
                const res = await fetch(`${API}/admin/receipts/render-pdf`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                    body: JSON.stringify({ sale_id: sale.id, vat_percent: vat, copies, ...docOpts }),
                });
                if (!res.ok) return;
                const blob = await res.blob();
                const u = URL.createObjectURL(blob);
                if (!revoked) {
                    oldUrl = pdfUrl;
                    setPdfUrl(u);
                }
            } catch {}
        })();
        return () => {
            revoked = true;
            if (oldUrl) URL.revokeObjectURL(oldUrl);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, sale, vat, copies, docType, currency]);

    const downloadPdf = async () => {
        setPrinting(true);
        try {
            await downloadAuthed("/admin/receipts/render-pdf", `receipt-${sale.id.slice(0,8)}.pdf`, null,
                { method: "POST", body: { sale_id: sale.id, vat_percent: vat, copies, ...docOpts } });
        } catch (e) { alert(e.message); }
        finally { setPrinting(false); }
    };

    const browserPrint = () => {
        if (!pdfUrl) return;
        const w = window.open(pdfUrl, "_blank");
        if (w) setTimeout(() => { try { w.focus(); w.print(); } catch {} }, 800);
        onPrinted?.("browser");
    };

    const sharePdf = async () => {
        if (!pdfUrl || !navigator.share) {
            alert("Share is not supported on this device. Use 'Save PDF' instead.");
            return;
        }
        try {
            const res = await fetch(pdfUrl);
            const blob = await res.blob();
            const file = new File([blob], `receipt-${sale.id.slice(0,8)}.pdf`, { type: "application/pdf" });
            await navigator.share({ files: [file], title: `Faturë ${sale.id.slice(0,8)}` });
        } catch (e) {
            if (e.name !== "AbortError") alert("Share failed: " + e.message);
        }
    };

    const getEscPosBytes = async () => {
        const token = getAuthToken();
        const res = await fetch(`${API}/admin/receipts/render-escpos`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({ sale_id: sale.id, vat_percent: vat, copies, cut_paper: cutPaper, open_cash_drawer: openDrawer, ...docOpts }),
        });
        if (!res.ok) throw new Error("Render failed: HTTP " + res.status);
        const { escpos_base64 } = await res.json();
        const bin = atob(escpos_base64);
        const bytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
        return bytes;
    };

    const connectBluetooth = async () => {
        if (!navigator.bluetooth) {
            alert("Web Bluetooth nuk suportohet në këtë browser. Përdor Chrome/Edge.");
            return null;
        }
        try {
            const device = await navigator.bluetooth.requestDevice({
                acceptAllDevices: true,
                optionalServices: [0xff00, 0xffe0, 0x18f0, "000018f0-0000-1000-8000-00805f9b34fb", "49535343-fe7d-4ae5-8fa9-9fafd205e455"],
            });
            await device.gatt.connect();
            setBtDevice(device);
            return device;
        } catch (e) {
            alert("Bluetooth pairing dështoi: " + e.message);
            return null;
        }
    };

    const printBluetooth = async () => {
        setPrinting(true);
        try {
            const device = btDevice || await connectBluetooth();
            if (!device) return;
            const bytes = await getEscPosBytes();
            const server = await device.gatt.connect();
            const services = await server.getPrimaryServices();
            let writeChar = null;
            for (const svc of services) {
                const chars = await svc.getCharacteristics();
                for (const ch of chars) {
                    if (ch.properties.write || ch.properties.writeWithoutResponse) { writeChar = ch; break; }
                }
                if (writeChar) break;
            }
            if (!writeChar) { alert("Nuk u gjet karakteristikë e shkruajtshme në printer."); return; }
            const CHUNK = 180;
            for (let i = 0; i < bytes.length; i += CHUNK) {
                const slice = bytes.slice(i, i + CHUNK);
                if (writeChar.properties.writeWithoutResponse) await writeChar.writeValueWithoutResponse(slice);
                else await writeChar.writeValue(slice);
            }
            onPrinted?.("bluetooth");
            alert("Fatura u dërgua në printer ✓");
        } catch (e) {
            alert("Printim Bluetooth dështoi: " + e.message);
        } finally { setPrinting(false); }
    };

    const printUSB = async () => {
        if (!navigator.usb) { alert("WebUSB nuk suportohet. Përdor Chrome/Edge në desktop."); return; }
        setPrinting(true);
        try {
            const dev = await navigator.usb.requestDevice({ filters: [] });
            try {
                await dev.open();
            } catch (openErr) {
                if (String(openErr.message || "").toLowerCase().includes("access denied")) {
                    alert(
                        "USB AKSES I REFUZUAR\n\n" +
                        "Printerin po e mban driver-i i Windows.\n\n" +
                        "Zgjidhjet:\n" +
                        "  1. PËRDOR BLUETOOTH (rekomanduar — funksionon menjëherë)\n" +
                        "  2. Ose kliko 'Print (Browser/A4)' → zgjidh printerin OCOM në driver-in e Windows\n" +
                        "  3. Ose instalo Zadig dhe zëvendëso driver-in me WinUSB (avancuar)"
                    );
                    return;
                }
                throw openErr;
            }
            if (dev.configuration === null) await dev.selectConfiguration(1);
            const iface = dev.configuration.interfaces[0];
            await dev.claimInterface(iface.interfaceNumber);
            const ep = iface.alternates[0].endpoints.find((e) => e.direction === "out");
            if (!ep) { alert("Nuk u gjet OUT endpoint në printer."); return; }
            const bytes = await getEscPosBytes();
            // Chunked transfer
            const CHUNK = 4096;
            for (let i = 0; i < bytes.length; i += CHUNK) {
                await dev.transferOut(ep.endpointNumber, bytes.slice(i, i + CHUNK));
            }
            await dev.close();
            onPrinted?.("usb");
            alert("Fatura u dërgua në printer USB ✓");
        } catch (e) {
            if (e.name !== "NotFoundError") {
                alert("Printim USB dështoi: " + e.message + "\n\nProvo me Bluetooth — funksionon pa probleme.");
            }
        } finally { setPrinting(false); }
    };

    if (!open || !sale) return null;

    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
            <div
                className="bg-white border-2 border-slate-900 shadow-[8px_8px_0_0_hsl(var(--primary))] w-full max-w-3xl max-h-[90vh] overflow-hidden flex"
                onClick={(e) => e.stopPropagation()}
                data-testid="receipt-printer-modal"
            >
                {/* Left: live preview */}
                <div className="flex-1 bg-slate-100 p-4 overflow-auto flex flex-col items-center">
                    <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-2">
                        Preview · 80mm
                    </div>
                    <div className="admin-light bg-white border border-slate-300 shadow-md" style={{ width: "302px" }}>
                        {pdfUrl ? (
                            <iframe
                                title="receipt-preview"
                                src={pdfUrl + "#toolbar=0&navpanes=0"}
                                className="w-full"
                                style={{ height: "480px", border: 0 }}
                                data-testid="receipt-preview-iframe"
                            />
                        ) : (
                            <div className="w-full h-[480px] flex items-center justify-center font-mono text-xs text-slate-400">
                                Duke ngarkuar…
                            </div>
                        )}
                    </div>
                </div>

                {/* Right: actions */}
                <div className="w-72 bg-white border-l border-slate-200 p-4 flex flex-col gap-3 overflow-y-auto">
                    <div className="flex items-center justify-between">
                        <div className="font-display font-black uppercase text-sm">Print Receipt</div>
                        <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-900" data-testid="receipt-close">
                            <X className="w-4 h-4" />
                        </button>
                    </div>

                    <div className="space-y-2 pt-2 border-t border-slate-200">
                        <label className="block text-[10px] font-mono uppercase tracking-wider text-slate-500">
                            TVSH %
                            <input
                                type="number" min="0" max="100" step="0.5" value={vat}
                                onChange={(e) => setVat(parseFloat(e.target.value) || 0)}
                                className="input-field !py-1 !text-xs mt-1"
                                data-testid="receipt-vat"
                            />
                        </label>
                        <label className="block text-[10px] font-mono uppercase tracking-wider text-slate-500">
                            Copies
                            <input
                                type="number" min="1" max="10" value={copies}
                                onChange={(e) => setCopies(parseInt(e.target.value) || 1)}
                                className="input-field !py-1 !text-xs mt-1"
                                data-testid="receipt-copies"
                            />
                        </label>
                        <label className="flex items-center gap-2 text-[10px] font-mono">
                            <input type="checkbox" checked={cutPaper} onChange={(e) => setCutPaper(e.target.checked)} data-testid="receipt-cut" />
                            Auto-cut letrën
                        </label>
                        <label className="flex items-center gap-2 text-[10px] font-mono">
                            <input type="checkbox" checked={openDrawer} onChange={(e) => setOpenDrawer(e.target.checked)} data-testid="receipt-drawer" />
                            Hap sirtarin e parave
                        </label>
                    </div>

                    <div className="pt-2 border-t border-slate-200 space-y-2">
                        <button onClick={printBluetooth} disabled={printing}
                            className="btn-primary w-full justify-center !py-2 !text-sm"
                            data-testid="receipt-bt-print">
                            <Bluetooth className="w-4 h-4" />
                            {btDevice ? "Print Bluetooth ✓" : "Print Bluetooth"}
                        </button>
                        <button onClick={printUSB} disabled={printing}
                            className="btn-outline w-full justify-center !py-2 !text-sm"
                            data-testid="receipt-usb-print">
                            <Usb className="w-4 h-4" /> Print USB
                        </button>
                        <button onClick={browserPrint} disabled={!pdfUrl || printing}
                            className="btn-outline w-full justify-center !py-2 !text-sm"
                            data-testid="receipt-browser-print">
                            <Printer className="w-4 h-4" /> Print (Browser / A4)
                        </button>
                    </div>

                    <div className="pt-2 border-t border-slate-200 space-y-2">
                        <button onClick={downloadPdf} disabled={printing}
                            className="btn-outline w-full justify-center !py-1.5 !text-xs"
                            data-testid="receipt-save-pdf">
                            <FileText className="w-3.5 h-3.5" /> Save PDF
                        </button>
                        <button onClick={sharePdf}
                            className="btn-outline w-full justify-center !py-1.5 !text-xs"
                            data-testid="receipt-share">
                            <Share2 className="w-3.5 h-3.5" /> Share
                        </button>
                    </div>

                    <div className="mt-auto pt-3 border-t border-slate-200 text-[9px] font-mono text-slate-500 leading-relaxed">
                        <div>OCOM OCPP-80T compatible.</div>
                        <div>ESC/POS · 80mm · 48 cols</div>
                        <div>Auto-paper-cut + barcode + QR included.</div>
                    </div>
                </div>
            </div>
        </div>
    );
}
